// packages/isomorphic/src/index.ts
import { differenceInYears } from "date-fns";

// packages/isomorphic/src/lib/auth-claims.ts
var MUST_CHANGE_PASSWORD_CLAIM = "mustChangePassword";

// packages/isomorphic/src/lib/format-file-size.ts
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

// packages/isomorphic/src/lib/format-mid.ts
function formatDoctorMid(mid) {
  return `DOC${String(mid).padStart(5, "0")}`;
}
function formatPatientMid(mid) {
  return `TC${String(mid).padStart(5, "0")}`;
}

// packages/isomorphic/src/lib/localized-speciality.ts
function translatedOrFallback(value, fallback = "") {
  const trimmed = value?.trim();
  return trimmed || fallback;
}
function getLocalizedSpeciality(speciality, language) {
  if (language === "en") {
    return {
      name: translatedOrFallback(speciality.nameEn, speciality.name),
      description: translatedOrFallback(
        speciality.descriptionEn,
        speciality.description ?? ""
      )
    };
  }
  return {
    name: speciality.name,
    description: speciality.description ?? ""
  };
}
function specialityMatchesQuery(speciality, language, query) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const localized = getLocalizedSpeciality(speciality, language);
  return [
    localized.name,
    localized.description,
    speciality.name,
    speciality.nameEn,
    speciality.description,
    speciality.descriptionEn
  ].some((value) => value?.toLowerCase().includes(normalizedQuery));
}

// packages/isomorphic/src/lib/notification-text.ts
var notificationTexts = {
  FOLLOWUP_AVAILABLE: "\u0627\u0644\u0645\u062A\u0627\u0628\u0639\u0629 \u0645\u062A\u0627\u062D\u0629",
  RESERVATION_CREATED: "\u062A\u0645 \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u062D\u062C\u0632",
  RESERVATION_CONFIRMED: "\u062A\u0645 \u062A\u0623\u0643\u064A\u062F \u0627\u0644\u062D\u062C\u0632",
  DOCTOR_PRESCRIBED_TREATMENT: "\u062A\u0645 \u0648\u0635\u0641 \u0627\u0644\u0639\u0644\u0627\u062C",
  DOCTOR_ASSIGNED: "\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0637\u0628\u064A\u0628",
  SECOND_OPINION_REPORT_SUBMITTED: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u062A\u0642\u0631\u064A\u0631 \u0627\u0644\u0631\u0623\u064A \u0627\u0644\u062B\u0627\u0646\u064A",
  RESERVATION_REVIEW_REQUESTED: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u062D\u062C\u0632",
  RESERVATION_STATUS_CHANGED: "\u062A\u0645 \u062A\u063A\u064A\u064A\u0631 \u062D\u0627\u0644\u0629 \u0627\u0644\u062D\u062C\u0632",
  DOCTOR_REQUESTED_INFO: "\u0637\u0644\u0628 \u0627\u0644\u0637\u0628\u064A\u0628 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629",
  RESERVATION_CANCELLATION: "\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062D\u062C\u0632",
  MEETING_SCHEDULED: "\u062A\u0645 \u062C\u062F\u0648\u0644\u0629 \u0627\u0644\u0627\u062C\u062A\u0645\u0627\u0639",
  MEETING_STARTED: "\u0628\u062F\u0623 \u0627\u0644\u0627\u062C\u062A\u0645\u0627\u0639",
  MEETING_ENDED: "\u0627\u0646\u062A\u0647\u0649 \u0627\u0644\u0627\u062C\u062A\u0645\u0627\u0639",
  FILE_UPLOADED: "\u062A\u0645 \u0631\u0641\u0639 \u0645\u0644\u0641",
  GENERAL_ANNOUNCEMENT: "\u0625\u0639\u0644\u0627\u0646 \u0639\u0627\u0645",
  FOLLOWUP_WINDOW_EXPIRED: "\u0627\u0646\u062A\u0647\u062A \u0641\u062A\u0631\u0629 \u0627\u0644\u0645\u062A\u0627\u0628\u0639\u0629",
  APPOINTMENT_REMINDER: "\u062A\u0630\u0643\u064A\u0631 \u0628\u0627\u0644\u0645\u0648\u0639\u062F",
  CASE_REFERRED: "\u062A\u0645\u062A \u0625\u062D\u0627\u0644\u0629 \u0627\u0644\u062D\u0627\u0644\u0629",
  CONSULTATION_COMPLETED: "\u0627\u0643\u062A\u0645\u0644\u062A \u0627\u0644\u0627\u0633\u062A\u0634\u0627\u0631\u0629",
  NO_SHOW: "\u0639\u062F\u0645 \u0627\u0644\u062D\u0636\u0648\u0631",
  REPORT_READY: "\u0627\u0644\u062A\u0642\u0631\u064A\u0631 \u062C\u0627\u0647\u0632",
  WELCOME: "\u0645\u0631\u062D\u0628\u064B\u0627 \u0628\u0643 \u0641\u064A \u0641\u0627\u064A",
  PRINTING_LINK_READY: "\u0631\u0627\u0628\u0637 \u0627\u0644\u0637\u0628\u0627\u0639\u0629 \u062B\u0644\u0627\u062B\u064A\u0629 \u0627\u0644\u0623\u0628\u0639\u0627\u062F \u062C\u0627\u0647\u0632"
};
var fallbackNotificationText = "\u0625\u0634\u0639\u0627\u0631 \u062C\u062F\u064A\u062F";

// packages/isomorphic/src/lib/quota.ts
var QUOTA_LOW_THRESHOLD = 2;
function quotaSeverity(count) {
  if (count <= 0) return "exhausted";
  if (count <= QUOTA_LOW_THRESHOLD) return "low";
  return "ok";
}

// packages/isomorphic/src/lib/retry-until.ts
import pRetry from "p-retry";
async function retryUntil(fn, predicate, options = {}) {
  const { onFailedAttempt, ...pRetryOptions } = options;
  return pRetry(
    async (attempt) => {
      const result = await fn();
      if (await predicate(result)) {
        return result;
      }
      throw new Error("Predicate not satisfied");
    },
    {
      ...pRetryOptions,
      onFailedAttempt: async (error) => {
        if (onFailedAttempt) {
          await onFailedAttempt(error, error.attemptNumber, error.retriesLeft);
        }
      }
    }
  );
}

// packages/isomorphic/src/lib/saudi-cities.ts
var SAUDI_CITIES = [
  "\u0627\u0644\u0631\u064A\u0627\u0636",
  "\u062C\u062F\u0629",
  "\u0645\u0643\u0629 \u0627\u0644\u0645\u0643\u0631\u0645\u0629",
  "\u0627\u0644\u0645\u062F\u064A\u0646\u0629 \u0627\u0644\u0645\u0646\u0648\u0631\u0629",
  "\u0627\u0644\u062F\u0645\u0627\u0645",
  "\u0627\u0644\u062E\u0628\u0631",
  "\u0627\u0644\u0638\u0647\u0631\u0627\u0646",
  "\u0627\u0644\u0637\u0627\u0626\u0641",
  "\u062A\u0628\u0648\u0643",
  "\u0628\u0631\u064A\u062F\u0629",
  "\u0623\u0628\u0647\u0627",
  "\u062C\u0627\u0632\u0627\u0646",
  "\u0646\u062C\u0631\u0627\u0646",
  "\u062D\u0627\u0626\u0644",
  "\u0627\u0644\u062C\u0628\u064A\u0644",
  "\u064A\u0646\u0628\u0639",
  "\u0627\u0644\u0623\u062D\u0633\u0627\u0621",
  "\u0627\u0644\u0642\u0637\u064A\u0641",
  "\u062E\u0645\u064A\u0633 \u0645\u0634\u064A\u0637",
  "\u0627\u0644\u062E\u0631\u062C"
];

// packages/isomorphic/src/lib/service-map.ts
var SERVICE_MAP = {
  standard: "\u0645\u0648\u0639\u062F \u0645\u0639 \u0637\u0628\u064A\u0628",
  second_opinion: "\u0631\u0623\u064A \u0637\u0628\u064A \u062B\u0627\u0646\u064D",
  homecare: "\u0631\u0639\u0627\u064A\u0629 \u0645\u0646\u0632\u0644\u064A\u0629"
};

// packages/isomorphic/src/lib/time-slots.ts
function overlap(slots) {
  const intervals = slots.map(({ startTime: start, endTime: end }) => ({
    start: Math.min(start, end),
    // Handle reversed times
    end: Math.max(start, end)
  }));
  for (let i = 1; i < intervals.length; i++) {
    for (let j = 0; j < i; j++) {
      if (intervals[i].start < intervals[j].end && intervals[i].end > intervals[j].start) {
        return i;
      }
    }
  }
  return -1;
}
var HOMECARE_SLOT_DURATION_MINUTES = 60;

// packages/isomorphic/src/lib/timezone.ts
function getTimezoneOffsetMinutes(timezone, date = /* @__PURE__ */ new Date()) {
  const utcStr = date.toLocaleString("en-US", { timeZone: "UTC" });
  const tzStr = date.toLocaleString("en-US", { timeZone: timezone });
  const utcDate = new Date(utcStr);
  const tzDate = new Date(tzStr);
  return (tzDate.getTime() - utcDate.getTime()) / 6e4;
}
function localMinutesToUtc(localMinutes, timezone) {
  const offset = getTimezoneOffsetMinutes(timezone);
  let utc = localMinutes - offset;
  if (utc < 0) utc += 1440;
  if (utc >= 1440) utc -= 1440;
  return utc;
}
function utcMinutesToLocal(utcMinutes, timezone) {
  const offset = getTimezoneOffsetMinutes(timezone);
  let local = utcMinutes + offset;
  if (local < 0) local += 1440;
  if (local >= 1440) local -= 1440;
  return local;
}
function utcMinutesToLocalDisplay(utcMinutes, timezone, hour12 = false) {
  const local = utcMinutesToLocal(utcMinutes, timezone);
  return formatMinutes(local, hour12);
}
function formatMinutes(minutes, hour12 = false) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!hour12) {
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${period}`;
}
var COMMON_TIMEZONES = [
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Bahrain",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Muscat",
  "Asia/Kuala_Lumpur",
  "Asia/Singapore",
  "Asia/Jakarta",
  "Africa/Cairo",
  "Europe/Istanbul",
  "Europe/London",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland"
];
function getAllTimezones() {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return COMMON_TIMEZONES;
  }
}

// packages/isomorphic/src/lib/user-time.ts
import { TZDate } from "@date-fns/tz";
import {
  addDays,
  addHours,
  addMinutes,
  endOfToday,
  format,
  formatDistanceToNow,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  subMinutes
} from "date-fns";
import { ar, enUS } from "date-fns/locale";
function getDocumentLang() {
  return globalThis.document?.documentElement.lang;
}
function getUserTimeLocale() {
  return getDocumentLang()?.startsWith("en") ? enUS : ar;
}
var userStartOfTomorrow = startOfTomorrow();
var dayStart = startOfToday();
var dayEnd = endOfToday();
var utcDayStartMinute = minutesFromUtcMidnight(startOfToday());
var utcDayEndMinute = minutesFromUtcMidnight(endOfToday());
function dateToTimeDisplay(date, hour12 = false) {
  return new Date(date).toLocaleTimeString(getUserTimeLocale().code ?? "ar", {
    hour: "2-digit",
    minute: "2-digit",
    hour12
  });
}
function minutesToTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}
var generateTimeSlots = (date = /* @__PURE__ */ new Date(), includeLastSlot = false) => {
  const slots = [];
  const workHoursStart = 0;
  const workHoursEnd = 24;
  const slotDuration = 30;
  const dayStart2 = startOfDay(date);
  let currentSlot = addHours(dayStart2, workHoursStart);
  const dayEnd2 = addHours(dayStart2, workHoursEnd);
  while (currentSlot < dayEnd2) {
    slots.push(currentSlot);
    currentSlot = addMinutes(currentSlot, slotDuration);
  }
  if (includeLastSlot) {
    slots.push(subMinutes(currentSlot, 1));
  }
  return slots;
};
function composeReservationDate(reservation) {
  return new TZDate(
    addMinutes(reservation.scheduledDate, reservation.minutesFrom),
    reservation.timezone
  );
}
function appointmentStartDate(reservation) {
  return addMinutes(
    new Date(reservation.scheduledDate),
    reservation.minutesFrom
  );
}
function hasAppointmentStarted(reservation) {
  return /* @__PURE__ */ new Date() >= appointmentStartDate(reservation);
}
function formatReservationDate(reservation) {
  return format(composeReservationDate(reservation), "EEEE - dd/MM/yyyy", {
    locale: getUserTimeLocale()
  });
}
function formatReservationTime(reservation) {
  return format(composeReservationDate(reservation), "h:mm a", {
    locale: getUserTimeLocale()
  });
}
function formatReservationDateTime(reservation) {
  return format(
    composeReservationDate(reservation),
    "EEEE - dd/MM/yyyy - h:mm a",
    { locale: getUserTimeLocale() }
  );
}
function formatDate(date) {
  return format(date, "EEEE - dd/MM/yyyy", { locale: getUserTimeLocale() });
}
function formatTime(date) {
  return format(date, "h:mm a", { locale: getUserTimeLocale() });
}
function formatDateTime(date) {
  return format(date, "EEEE - dd/MM/yyyy - h:mm a", {
    locale: getUserTimeLocale()
  });
}
function formatBirthDate(date) {
  return format(date, "PPP", { locale: getUserTimeLocale() });
}
function formatDateShort(date) {
  return format(date, "dd/MM/yyyy", { locale: getUserTimeLocale() });
}
function formatDateTimeShort(date) {
  return format(date, "dd/MM/yyyy HH:mm", { locale: getUserTimeLocale() });
}
function formatRelativeTime(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: getUserTimeLocale()
  });
}
function minutesFromUtcMidnight(date) {
  const localMinutes = date.getHours() * 60 + date.getMinutes();
  const offsetMinutes = date.getTimezoneOffset();
  const raw = localMinutes + offsetMinutes;
  if (raw < 0) return raw;
  if (raw >= 1440) return raw - 1440;
  return raw;
}
function formatSecondOpinionDueDate(request) {
  if (request.urgent) {
    return formatDate(addDays(startOfToday(), 2));
  }
  return formatDate(addDays(startOfToday(), 13));
}

// packages/isomorphic/src/lib/validators.ts
import { parseISO } from "date-fns";
import parsePhoneNumber from "libphonenumber-js";
import { z } from "zod";
var validatePhoneNumber = async (value) => {
  const phoneNumber = parsePhoneNumber(value);
  return phoneNumber && phoneNumber.isValid();
};
var phoneValidator = (options) => async (value, ctx) => {
  try {
    const isEmpty = !value || value.length === 0;
    if (isEmpty && !options.validateEmpty) {
      return true;
    }
    const isValid = await validatePhoneNumber(value);
    if (!isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "\u064A\u064F\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644 \u0628\u0635\u064A\u063A\u0629 \u0627\u0644\u062F\u0648\u0644\u064A\u0629 (966XXXXXXXXX+) \u0645\u0639 \u0631\u0645\u0632 \u0627\u0644\u062F\u0648\u0644\u0629. \u0645\u062B\u0627\u0644: (966501234567+)"
      });
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "\u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644 \u063A\u064A\u0631 \u0635\u062D\u064A\u062D"
    });
    return false;
  }
};
var futureDateSchema = z.string().datetime().transform((date) => parseISO(date)).refine(
  (date) => {
    const nowUTC = /* @__PURE__ */ new Date();
    return date >= nowUTC;
  },
  {
    message: "Date must not be in the past"
  }
);
var stringBoolean = z.coerce.string().or(z.coerce.boolean()).nullish().transform((value) => {
  if (value === null || value === void 0) {
    return false;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return false;
});
var pageNumberSchema = z.coerce.number().int().positive().nullish().default(1).transform((value) => value === null || value === void 0 ? 1 : value);
var pageSizeSchema = z.coerce.number().int().min(1).max(100).nullish().default(50).transform((value) => value === null || value === void 0 ? 50 : value);
var searchQuerySchema = z.union([
  // Numeric-start query: any length
  z.string().trim().max(100).regex(/^\d/),
  // Text query: minimum 3 characters
  z.string().trim().min(3).max(100),
  // Empty after trim -> undefined
  z.string().trim().max(0).transform(() => void 0)
]).optional();
var sortOrderSchema = z.enum(["asc", "desc"]).optional().default("asc");
var scheduledMinutesSchema = z.coerce.number().int().describe(
  "Scheduled minutes from the start of the day in utc. get this from doctor availability."
);
var clearWhitespaces = (value) => value.replace(/\s/g, "");

// packages/isomorphic/src/lib/weekday.ts
var AR_DAYS_OF_WEEK = [
  "\u0627\u0644\u0633\u0628\u062A",
  "\u0627\u0644\u0623\u062D\u062F",
  "\u0627\u0644\u0627\u062B\u0646\u064A\u0646",
  "\u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621",
  "\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621",
  "\u0627\u0644\u062E\u0645\u064A\u0633",
  "\u0627\u0644\u062C\u0645\u0639\u0629"
];
var EN_DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];
var EN_AR_DAYS_OF_WEEK = {
  SUNDAY: "\u0627\u0644\u0623\u062D\u062F",
  MONDAY: "\u0627\u0644\u0627\u062B\u0646\u064A\u0646",
  TUESDAY: "\u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621",
  WEDNESDAY: "\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621",
  THURSDAY: "\u0627\u0644\u062E\u0645\u064A\u0633",
  FRIDAY: "\u0627\u0644\u062C\u0645\u0639\u0629",
  SATURDAY: "\u0627\u0644\u0633\u0628\u062A"
};
var AR_TO_EN_DAYS_OF_WEEK = {
  \u0627\u0644\u0633\u0628\u062A: "SATURDAY",
  \u0627\u0644\u0623\u062D\u062F: "SUNDAY",
  \u0627\u0644\u0627\u062B\u0646\u064A\u0646: "MONDAY",
  \u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621: "TUESDAY",
  \u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621: "WEDNESDAY",
  \u0627\u0644\u062E\u0645\u064A\u0633: "THURSDAY",
  \u0627\u0644\u062C\u0645\u0639\u0629: "FRIDAY"
};

// packages/isomorphic/src/index.ts
function timelineContext(entry) {
  if (!entry?.context) return {};
  try {
    const parsed = JSON.parse(entry.context);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
function getPatientDemographics(timeline, eventType = "RESERVATION_CREATED") {
  const ctx = timelineContext(timeline.find((it) => it.type === eventType));
  return ctx.patientDemographics ?? void 0;
}
function getDoctorName(timeline, eventType = "RESERVATION_CREATED") {
  const ctx = timelineContext(timeline.find((it) => it.type === eventType));
  return typeof ctx.doctorName === "string" && ctx.doctorName.length > 0 ? ctx.doctorName : void 0;
}
function getGuestEmail(timeline, eventType = "RESERVATION_CREATED") {
  const ctx = timelineContext(timeline.find((it) => it.type === eventType));
  return typeof ctx.guestEmail === "string" && ctx.guestEmail.length > 0 ? ctx.guestEmail : void 0;
}
function isNullOrUndefined(value) {
  return value === void 0 || value === null;
}
function notNullOrUndefined(value) {
  return !isNullOrUndefined(value);
}
function displayName(user) {
  if (!user) {
    return "";
  }
  if (user.b2bProfile) {
    return user.b2bProfile?.name || "";
  }
  return `${user.b2cProfile?.firstName || ""} ${user.b2cProfile?.parentName || ""} ${user.b2cProfile?.lastName || ""}`.trim();
}
function toAge(input) {
  return input?.birthDate ? differenceInYears(/* @__PURE__ */ new Date(), input.birthDate) : void 0;
}
export {
  AR_DAYS_OF_WEEK,
  AR_TO_EN_DAYS_OF_WEEK,
  EN_AR_DAYS_OF_WEEK,
  EN_DAYS_OF_WEEK,
  HOMECARE_SLOT_DURATION_MINUTES,
  MUST_CHANGE_PASSWORD_CLAIM,
  QUOTA_LOW_THRESHOLD,
  SAUDI_CITIES,
  SERVICE_MAP,
  appointmentStartDate,
  clearWhitespaces,
  composeReservationDate,
  dateToTimeDisplay,
  dayEnd,
  dayStart,
  displayName,
  fallbackNotificationText,
  formatBirthDate,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatDateTimeShort,
  formatDoctorMid,
  formatFileSize,
  formatMinutes,
  formatPatientMid,
  formatRelativeTime,
  formatReservationDate,
  formatReservationDateTime,
  formatReservationTime,
  formatSecondOpinionDueDate,
  formatTime,
  futureDateSchema,
  generateTimeSlots,
  getAllTimezones,
  getDoctorName,
  getGuestEmail,
  getLocalizedSpeciality,
  getPatientDemographics,
  getTimezoneOffsetMinutes,
  getUserTimeLocale,
  hasAppointmentStarted,
  isNullOrUndefined,
  localMinutesToUtc,
  minutesFromUtcMidnight,
  minutesToTime,
  notNullOrUndefined,
  notificationTexts,
  overlap,
  pageNumberSchema,
  pageSizeSchema,
  phoneValidator,
  quotaSeverity,
  retryUntil,
  scheduledMinutesSchema,
  searchQuerySchema,
  sortOrderSchema,
  specialityMatchesQuery,
  stringBoolean,
  timelineContext,
  toAge,
  userStartOfTomorrow,
  utcDayEndMinute,
  utcDayStartMinute,
  utcMinutesToLocal,
  utcMinutesToLocalDisplay,
  validatePhoneNumber
};
//# sourceMappingURL=index.js.map
